import { useCallback, useEffect, useRef, useState, type ChangeEvent, type MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, Mail, Pencil, UserRound } from 'lucide-react'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { useDeleteProfileMutation, useGetProfileQuery, useUpdateProfileMutation, useUploadAvatarMutation } from '@/store/api/profileApi'
import { useLogout } from '@/hooks/use-logout'
import { logout as logoutAction } from '@/store/slices/authSlice'
import { showApiErrorToast } from '@/lib/api-toast'
import { formatAmount, formatPercent } from '@/lib/format-number'
import { resolvePlayerAssetUrl } from '@/lib/player-api'
import { cn } from '@/lib/utils'
import { PlayerStatCard } from './PlayerStatCard'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectIsAuthenticated } from '@/store/slices/authSlice'

const DEFAULT_AVATAR_SRC = '/images/avatars/default-avatar.png'
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MAX_AVATAR_FILE_SIZE_BYTES = 5 * 1024 * 1024

export function PlayerCard() {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const logout = useLogout()
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const [isClosingAccount, setIsClosingAccount] = useState(false)
	const { data: profileResponse, isLoading } = useGetProfileQuery(undefined, { skip: !isAuthenticated || isClosingAccount })
	const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateProfileMutation()
	const [deleteProfile, { isLoading: isDeletingProfile }] = useDeleteProfileMutation()
	const [uploadAvatar, { isLoading: isUploadingAvatar }] = useUploadAvatarMutation()
	const avatarInputRef = useRef<HTMLInputElement | null>(null)
	const profile = profileResponse?.data
	const avatarUrl = profile?.avatar_url ? resolvePlayerAssetUrl(profile?.avatar_url) : DEFAULT_AVATAR_SRC
	const username = profile?.username || 'Player'
	const email = profile?.email || ''
	const [isEditing, setIsEditing] = useState(false)
	const [draftUsername, setDraftUsername] = useState(username)
	const [draftEmail, setDraftEmail] = useState(email)
	const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
	const [isCloseAccountDialogOpen, setIsCloseAccountDialogOpen] = useState(false)

	const trimmedDraftUsername = draftUsername.trim()
	const trimmedDraftEmail = draftEmail.trim()
	const hasChanges = trimmedDraftUsername !== username || trimmedDraftEmail !== email
	const canSave = hasChanges && trimmedDraftUsername.length > 0 && EMAIL_PATTERN.test(trimmedDraftEmail) && !isUpdatingProfile
	const profileId = profile?.id
	const totalWagerAmount = profile?.total_wager_amount ?? 0
	const totalWinAmount = profile?.total_win_amount ?? 0
	const totalLossAmount = profile?.total_loss_amount ?? 0
	const totalResolvedAmount = totalWinAmount + totalLossAmount
	const winningRate = totalResolvedAmount > 0 ? (totalWinAmount / totalResolvedAmount) * 100 : 0
	const totalProfit = totalWinAmount - totalWagerAmount

	const resetDraft = useCallback(() => {
		setDraftUsername(username)
		setDraftEmail(email)
	}, [email, username])

	useEffect(() => {
		if (!isEditing) {
			resetDraft()
		}
	}, [isEditing, resetDraft])

	useEffect(() => {
		if (!isEditing) {
			return
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') {
				return
			}

			setIsSaveDialogOpen(false)
			setIsCloseAccountDialogOpen(false)
			resetDraft()
			setIsEditing(false)
		}

		window.addEventListener('keydown', handleKeyDown)

		return () => {
			window.removeEventListener('keydown', handleKeyDown)
		}
	}, [isEditing, resetDraft])

	const handleEnterEdit = () => {
		resetDraft()
		setIsEditing(true)
	}

	const handleUsernameChange = (event: ChangeEvent<HTMLInputElement>) => {
		setDraftUsername(event.target.value)
	}

	const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
		setDraftEmail(event.target.value)
	}

	const handleSaveClick = () => {
		if (!canSave) {
			return
		}

		setIsSaveDialogOpen(true)
	}

	const handleConfirmSave = () => {
		if (!canSave) {
			return
		}

		void updateProfile({
			username: trimmedDraftUsername,
			email: trimmedDraftEmail,
		})
			.unwrap()
			.then(() => {
				setIsSaveDialogOpen(false)
				setIsEditing(false)
			})
			.catch(() => {
				// Errors are handled by the global API interceptor.
			})
	}

	const handleCloseAccountClick = () => {
		setIsCloseAccountDialogOpen(true)
	}

	const handleAvatarPickerOpen = () => {
		if (isUploadingAvatar) {
			return
		}

		avatarInputRef.current?.click()
	}

	const handleAvatarFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		event.target.value = ''

		if (!file || isUploadingAvatar) {
			return
		}

		if (!file.type.startsWith('image/')) {
			showApiErrorToast('Please select a valid image file.')
			return
		}

		if (file.size > MAX_AVATAR_FILE_SIZE_BYTES) {
			showApiErrorToast('Avatar image must be 5MB or smaller.')
			return
		}

		const body = new FormData()
		body.append('avatar', file)

		void uploadAvatar(body)
			.unwrap()
			.catch(() => {
				// Errors are handled by the global API interceptor.
			})
	}

	const handleConfirmCloseAccount = () => {
		setIsClosingAccount(true)

		void deleteProfile()
			.unwrap()
			.then(() => {
				setIsCloseAccountDialogOpen(false)
				dispatch(logoutAction())
				navigate('/')
			})
			.catch(() => {
				setIsClosingAccount(false)
				// Errors are handled by the global API interceptor.
			})
	}

	const handleConfirmSaveClick = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		handleConfirmSave()
	}

	const handleConfirmCloseAccountClick = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		handleConfirmCloseAccount()
	}

	if (isEditing) {
		return (
			<section className='relative overflow-visible rounded-2xl border border-border bg-card shadow-[0_4px_12px_0_hsl(var(--background)/0.2)]'>
				<div className='relative rounded-2xl border-b border-border bg-[radial-gradient(circle_at_50%_0%,hsl(var(--primary)/0.22),transparent_32%),hsl(var(--card))] px-4 pb-7 pt-16 sm:px-8'>
					<div className='absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2'>
						<div className='relative rounded-full border-[3px] border-primary bg-background p-1'>
							<img
								src={avatarUrl}
								alt={username}
								className={cn('h-[88px] w-[88px] rounded-full object-cover sm:h-[96px] sm:w-[96px]', (isLoading || isUploadingAvatar) && 'opacity-60')}
							/>
							<input ref={avatarInputRef} type='file' accept='image/*' className='sr-only' onChange={handleAvatarFileChange} />
							<button
								type='button'
								onClick={handleAvatarPickerOpen}
								disabled={isUploadingAvatar}
								aria-label='Upload profile avatar'
								className={cn(
									'absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full border-2 border-card bg-primary text-background transition-opacity',
									isUploadingAvatar ? 'cursor-not-allowed opacity-70' : 'hover:opacity-90',
								)}
							>
								<Pencil className='h-4 w-4' />
							</button>
						</div>
					</div>

					<div className='mt-4 grid gap-4 sm:grid-cols-2'>
						<label className='relative block'>
							<span className='sr-only'>Username</span>
							<UserRound className='pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground' />
							<Input
								value={draftUsername}
								onChange={handleUsernameChange}
								placeholder='Username'
								autoComplete='username'
								className='h-14 rounded-xl border-border bg-secondary pl-14 text-base font-semibold tracking-[-0.24px] text-foreground focus-visible:ring-primary/40'
							/>
						</label>

						<label className='relative block'>
							<span className='sr-only'>Email</span>
							<Mail className='pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground' />
							<Input
								value={draftEmail}
								onChange={handleEmailChange}
								type='email'
								placeholder='Email address'
								autoComplete='email'
								className='h-14 rounded-xl border-border bg-secondary pl-14 text-base font-semibold tracking-[-0.24px] text-foreground focus-visible:ring-primary/40'
							/>
						</label>
					</div>

					<button
						type='button'
						onClick={handleSaveClick}
						disabled={!canSave}
						className={cn(
							'relative mx-auto mt-7 flex h-14 w-full max-w-xs flex-col items-center justify-center rounded-xl border border-primary-border bg-primary px-6 text-base font-semibold tracking-[-0.24px] text-background shadow-[0_10px_30px_0_hsl(var(--primary)/0.14)] transition-opacity',
							canSave ? 'hover:opacity-90' : 'cursor-not-allowed opacity-50',
						)}
					>
						<span>Save changes</span>
						<span className='mt-1 h-0.5 w-10 rounded-full bg-background/80' aria-hidden />
					</button>
				</div>

				<div className='flex flex-col gap-3 rounded-b-2xl bg-destructive/10 px-4 py-5 text-sm font-medium tracking-[-0.21px] text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-8'>
					<span>ID: {profileId ?? '-'}</span>
					<button
						type='button'
						onClick={handleCloseAccountClick}
						className='self-start underline underline-offset-4 transition-colors hover:text-destructive sm:self-auto'
					>
						Close account
					</button>
				</div>

				<AlertDialog open={isSaveDialogOpen} onOpenChange={setIsSaveDialogOpen}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Save profile changes?</AlertDialogTitle>
							<AlertDialogDescription>This will update your username and email address.</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel disabled={isUpdatingProfile}>Cancel</AlertDialogCancel>
							<AlertDialogAction onClick={handleConfirmSaveClick} disabled={isUpdatingProfile}>
								Save changes
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				<AlertDialog open={isCloseAccountDialogOpen} onOpenChange={setIsCloseAccountDialogOpen}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Close account?</AlertDialogTitle>
							<AlertDialogDescription>This action will close your account. You will be logged out after it succeeds.</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel disabled={isDeletingProfile}>Cancel</AlertDialogCancel>
							<AlertDialogAction
								onClick={handleConfirmCloseAccountClick}
								disabled={isDeletingProfile}
								className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
							>
								Close account
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</section>
		)
	}

	return (
		<section className='relative overflow-visible rounded-2xl border border-border bg-card px-4 pb-6 pt-14 shadow-[0_4px_12px_0_hsl(var(--background)/0.2)] sm:px-8'>
			<div className='absolute left-4 top-4 sm:left-6'>
				<button
					type='button'
					aria-label='Edit profile'
					onClick={handleEnterEdit}
					className='flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-secondary text-foreground transition-opacity hover:opacity-80'
				>
					<Pencil className='h-4 w-4' />
				</button>
			</div>

			<div className='absolute right-4 top-4 sm:right-6'>
				<button
					type='button'
					aria-label='Logout'
					onClick={logout}
					className='flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-secondary text-foreground transition-opacity hover:opacity-80'
				>
					<LogOut className='h-4 w-4' />
				</button>
			</div>

			<div className='absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2'>
				<div className='rounded-full border-[3px] border-primary bg-background p-1'>
					<img src={avatarUrl} alt={username} className={cn('h-[88px] w-[88px] rounded-full object-cover sm:h-[96px] sm:w-[96px]', isLoading && 'opacity-60')} />
				</div>
			</div>

			<div className='mt-2 text-center'>
				<h1 className='text-xl font-semibold tracking-[-0.3px] text-foreground sm:text-2xl'>{username}</h1>
			</div>

			<div className='mt-6 flex flex-col gap-3 sm:flex-row sm:gap-4'>
				<PlayerStatCard label='Winning rate' value={formatPercent(winningRate)} tone='amber' />
				<PlayerStatCard label='Total profit' value={formatAmount(totalProfit)} tone='green' />
			</div>
		</section>
	)
}
